import classNames from 'classnames';

import Select from '../../../Select';
import type { SelectOption } from '../../../Select';
import OverlayTriggerTooltip from '../../../OverlayTriggerTooltip';

import { InfoCircleIcon } from '../../../../icons';

import { TRIP_ATTRIBUTE_TOOLTIP } from '../../utilities/constants';
import type { FacetAddGroup } from '../../utilities/helpers';

import styles from './_index.module.scss';

const QuestionOptionLabel = ({ label }: { label: string }) => (
  <OverlayTriggerTooltip content={label} placement="right">
    <span className={styles['question-option-label']}>{label}</span>
  </OverlayTriggerTooltip>
);

export type AddFacetSelectProps = {
  'aria-label': string;
  className?: string;
  'data-testid'?: string;
  groups: FacetAddGroup[];
  /** Optional flat entry rendered first, e.g. Compare By's "Overall (no cut)". */
  leadingOption?: SelectOption;
  /** Receives the chosen facet id, or '' for the leading option. */
  onSelect: (facetId: string) => void;
  placeholder: string;
  /** `null` lets the picker reset to its placeholder after each pick (Filters). */
  value: null | SelectOption;
};

/**
 * Grouped "+ Add …" picker shared by Filters and Compare By. Renders facet
 * groups in fixed CATEGORY_ORDER via the design-system Select's grouped
 * options, with an info tooltip on the Trip attributes group header.
 */
const AddFacetSelect = ({
  'aria-label': ariaLabel,
  className,
  'data-testid': dataTestId,
  groups,
  leadingOption,
  onSelect,
  placeholder,
  value,
}: AddFacetSelectProps) => {
  const options: SelectOption[] = [
    ...(leadingOption ? [leadingOption] : []),
    ...groups.map((group) => ({
      label:
        group.category === 'trip' ? (
          <span className={styles['group-label']}>
            {group.label}
            <OverlayTriggerTooltip content={TRIP_ATTRIBUTE_TOOLTIP}>
              <InfoCircleIcon aria-label="About trip attributes" className={styles['group-info']} />
            </OverlayTriggerTooltip>
          </span>
        ) : (
          group.label
        ),
      options: group.options.map((option) => ({
        ...option,
        label:
          group.category === 'question' ? (
            <QuestionOptionLabel label={option.label} />
          ) : (
            option.label
          ),
      })),
    })),
  ];

  return (
    <Select
      aria-label={ariaLabel}
      className={classNames(styles['select'], className)}
      data-testid={dataTestId}
      // The sidebar host is an `overflow: auto` box; portal the menu so it is
      // not clipped by it (and does not force a horizontal scrollbar).
      menuInPortal
      onChange={(option) => onSelect((option as null | { value?: string })?.value ?? '')}
      options={options}
      placeholder={placeholder}
      value={value}
    />
  );
};

export default AddFacetSelect;
